// API helpers for image generation, vision analysis, and Runway video.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const fetchCapabilities = async () => {
  try {
    const r = await fetch(`${API}/capabilities`);
    if (!r.ok) return {};
    return await r.json();
  } catch (_) {
    return {};
  }
};

export const generateStabilityImage = async ({ prompt, model = 'core', aspectRatio = '16:9', outputFormat = 'png', negativePrompt = '' }) => {
  const resp = await fetch(`${API}/image/stability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      model,
      aspect_ratio: aspectRatio,
      output_format: outputFormat,
      negative_prompt: negativePrompt,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Stability ${resp.status}: ${txt.slice(0, 250)}`);
  }
  return resp.json(); // { image_b64, mime, seed, finish_reason, credits_left }
};

export const generateSora2Video = async ({ prompt, model = 'sora-2', size = '1280x720', seconds = '4' }) => {
  const resp = await fetch(`${API}/video/sora2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model, size, seconds }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    let parsed = txt;
    try {
      const j = JSON.parse(txt);
      parsed = j.detail || txt;
    } catch (err) {
      console.debug('Sora2: response not JSON, using raw text', err);
    }
    throw new Error(`Sora 2 ${resp.status}: ${typeof parsed === 'string' ? parsed.slice(0, 350) : JSON.stringify(parsed).slice(0, 350)}`);
  }
  return resp.json(); // { video_b64, mime, ... }
};

export const generateImage = async ({ prompt, referenceImageBase64 = null, model = 'gemini-3.1-flash-image-preview' }) => {
  const resp = await fetch(`${API}/image/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      reference_image_base64: referenceImageBase64,
      model,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Image gen error ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return resp.json(); // { text, images: [{ mime_type, data_base64 }] }
};

export const generatePollinationsImage = async ({ prompt, model = 'flux', width = 1024, height = 1024, seed = null, enhance = false }) => {
  const resp = await fetch(`${API}/image/pollinations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model, width, height, seed, enhance, nologo: true }),
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(`Pollinations ${resp.status}: ${t.slice(0, 250)}`);
  }
  return resp.json();
};

export const ttsEdge = async ({ text, voice = 'fr-FR-DeniseNeural', rate = '+0%', volume = '+0%', pitch = '+0Hz' }) => {
  const resp = await fetch(`${API}/tts/edge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, voice, rate, volume, pitch }),
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(`Edge TTS ${resp.status}: ${t.slice(0, 200)}`);
  }
  return resp.json(); // { audio_b64, mime, voice }
};

export const fetchTtsVoices = async (locale = 'fr') => {
  try {
    const r = await fetch(`${API}/tts/voices?locale=${encodeURIComponent(locale)}`);
    if (!r.ok) return { count: 0, voices: [] };
    return r.json();
  } catch (_) {
    return { count: 0, voices: [] };
  }
};

export const generateAudioStudio = async () => {
  const resp = await fetch(`${API}/audio/studio`, { method: 'POST' });
  if (!resp.ok) throw new Error(`Audio Studio ${resp.status}`);
  return resp.json();
};

export const analyzeVision = async ({ imageBase64, question, model = 'gpt-4o' }) => {
  const resp = await fetch(`${API}/vision/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      image_base64: imageBase64,
      question,
      model,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Vision error ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return resp.json(); // { text }
};

export const generateVideo = async ({ promptImageBase64, promptText = '', duration = 5, ratio = '1280:768' }) => {
  const resp = await fetch(`${API}/video/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt_image_base64: promptImageBase64,
      prompt_text: promptText,
      duration,
      ratio,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Runway error ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return resp.json(); // { task_id, raw }
};

export const getVideoStatus = async (taskId) => {
  const resp = await fetch(`${API}/video/status/${encodeURIComponent(taskId)}`);
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Status error ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return resp.json();
};

/**
 * Poll a Runway task until it finishes or fails.
 * Calls onProgress({status, progress}) along the way.
 */
export const webSearch = async (query, maxResults = 6) => {
  const resp = await fetch(`${API}/search/web`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, max_results: maxResults }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Search error ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return resp.json(); // { query, results: [{title, url, snippet}] }
};

export const pollVideoUntilDone = async (taskId, { onProgress, maxWaitMs = 5 * 60 * 1000, intervalMs = 4000 } = {}) => {
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    const data = await getVideoStatus(taskId);
    onProgress?.({ status: data.status, progress: data.progress });
    if (data.status === 'SUCCEEDED') return data;
    if (data.status === 'FAILED') {
      throw new Error(data.failure || 'Runway task failed');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('Délai dépassé (5 min) — la vidéo prend trop de temps');
};
